import { isPlatformBrowser } from '@angular/common';
import { Injectable, InjectionToken, PLATFORM_ID, inject } from '@angular/core';
import mixpanel, { type Config, type RequestOptions } from 'mixpanel-browser';
import { firstValueFrom } from 'rxjs';
import { User, UserRole } from '../models/user.model';
import { MockToggleService } from '../services/mock-toggle.service';
import { ConsentService } from './consent.service';
import { MIXPANEL_CONFIG } from './mixpanel.config';

/**
 * SDK surface used by the app. This is the ONLY module allowed to import 'mixpanel-browser'.
 * Specs provide a fake through MIXPANEL_SDK instead of vi.mock('mixpanel-browser'), because
 * module mocks of node_modules are ignored when specs share a single worker.
 */
export type MixpanelSdk = Pick<
  typeof mixpanel,
  | 'init'
  | 'identify'
  | 'track'
  | 'reset'
  | 'get_distinct_id'
  | 'start_session_recording'
  | 'stop_session_recording'
>;

export const MIXPANEL_SDK = new InjectionToken<MixpanelSdk>('MIXPANEL_SDK', {
  providedIn: 'root',
  factory: () => mixpanel,
});

/** Properties sent with every event (values lower case, no "_"). */
export interface MixpanelBaseProps {
  userRole: UserRole;
  dataMode: 'api' | 'mock';
  appEnvironment: string;
}

/**
 * SHA-256 hex of the e-mail (trimmed, lower-cased), used as Mixpanel distinct_id.
 * Returns null when there is no e-mail or Web Crypto is unavailable: the plain e-mail is never used.
 */
export async function hashIdentity(email: string | null | undefined): Promise<string | null> {
  const normalized = (email ?? '').trim().toLowerCase();
  const subtle = globalThis.crypto?.subtle;
  if (!normalized || !subtle) return null;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(normalized));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Max events kept while the init for the signed-in user is still running. */
export const MIXPANEL_PENDING_LIMIT = 20;
/**
 * The init is started by an effect on AuthService.currentUser(), which runs on the next change
 * detection. Events tracked right before that (e.g. DD Login after a token validation) are kept
 * for this short window; if no init has started by then, they are dropped.
 */
export const MIXPANEL_PENDING_GRACE_MS = 50;

/** Per-event delivery options. */
export interface TrackOptions {
  /**
   * Send right away with navigator.sendBeacon instead of the SDK's batched XHR, so the event
   * survives an immediate full-page redirect (e.g. logout to SSO).
   */
  beacon?: boolean;
}

const BEACON_REQUEST: RequestOptions = { transport: 'sendBeacon', send_immediately: true };

interface PendingEvent {
  event: string;
  props: Record<string, unknown>;
  options: TrackOptions;
}

/**
 * Wrapper around mixpanel-browser. Every SDK call is guarded and wrapped in try/catch so
 * analytics can never break the app. With an empty token everything is a silent no-op.
 */
@Injectable({ providedIn: 'root' })
export class MixPanelService {
  private readonly sdk = inject(MIXPANEL_SDK);
  private readonly config = inject(MIXPANEL_CONFIG);
  private readonly consent = inject(ConsentService);
  private readonly mockToggle = inject(MockToggleService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** mixpanel.init() was called (it can only run once per page load). */
  private sdkLoaded = false;
  /** A signed-in, non-opted-out user is identified: tracking and replay are allowed. */
  private active = false;
  private initRun?: Promise<void>;
  /** Bumped on reset() so an init still in flight for the previous user is dropped. */
  private generation = 0;
  private userRole: UserRole = 'viewer';
  /** Replay sampling, decided once per page session. */
  private replaySampled?: boolean;
  private replayRequested = false;
  private recording = false;
  /** Events tracked while the init is in flight, flushed in order once the service is active. */
  private pending: PendingEvent[] = [];
  /** Set when the last init ended without activation (opt-out, error): nothing is buffered. */
  private bufferClosed = false;
  private graceTimer?: ReturnType<typeof setTimeout>;

  /** True when this build should talk to Mixpanel at all. */
  get enabled(): boolean {
    return this.isBrowser && !!this.config.token;
  }

  /** True once the current user is identified and allowed to be tracked. */
  get isActive(): boolean {
    return this.active;
  }

  /**
   * Initializes Mixpanel for the signed-in user (single init: repeated calls only refresh
   * the role). Checks the GDPR opt-out first; an opted-out user never reaches the SDK.
   */
  init(user: Pick<User, 'email' | 'role'>): Promise<void> {
    this.userRole = user.role;
    if (!this.enabled || this.active) return Promise.resolve();
    if (!this.initRun) {
      this.bufferClosed = false;
      const generation = this.generation;
      this.initRun = this.runInit(user.email, generation).finally(() => {
        if (generation === this.generation) this.initRun = undefined;
      });
    }
    return this.initRun;
  }

  /** Keeps the userRole base property in sync after a role switch. */
  setUserRole(role: UserRole): void {
    this.userRole = role;
  }

  /** Sends an event with the base properties; event-specific props go in subProperties. */
  track(event: string, props: Record<string, unknown> = {}, options: TrackOptions = {}): void {
    if (this.active) {
      this.send(event, props, options);
      return;
    }
    this.buffer(event, props, options);
  }

  /**
   * Starts session replay if this page session was sampled and the user may be tracked.
   * If init is still running, recording starts as soon as it finishes.
   */
  startSessionRecording(): void {
    if (!this.enabled || !this.config.enableReplay || !this.isReplaySampled()) return;
    this.replayRequested = true;
    if (!this.active || this.recording) return;
    try {
      this.sdk.start_session_recording();
      this.recording = true;
    } catch (error) {
      console.error('mixpanel replay error', error);
    }
  }

  stopSessionRecording(): void {
    this.replayRequested = false;
    if (!this.recording) return;
    this.recording = false;
    try {
      this.sdk.stop_session_recording();
    } catch {
      // Ignore: the recorder may already be stopped.
    }
  }

  /** Call on logout: stops replay, clears the Mixpanel identity and the consent cache. */
  reset(): void {
    this.generation++;
    this.initRun = undefined;
    this.stopSessionRecording();
    this.active = false;
    this.userRole = 'viewer';
    this.dropPending();
    this.bufferClosed = false;
    this.consent.reset();
    if (!this.sdkLoaded) return;
    try {
      this.sdk.reset();
    } catch {
      // Ignore SDK errors on logout.
    }
  }

  private async runInit(email: string, generation: number): Promise<void> {
    try {
      // Hash first: the consent cache is tied to the hashed identity.
      const distinctId = await hashIdentity(email);
      if (generation !== this.generation) return;

      const optedOut = await firstValueFrom(this.consent.checkOptOut(distinctId));
      if (generation !== this.generation) return;
      if (optedOut) {
        this.closeBuffer();
        return;
      }

      if (!this.sdkLoaded) {
        this.sdk.init(this.config.token, this.sdkConfig());
        this.sdkLoaded = true;
      }
      // localStorage persistence keeps the previous distinct_id across page loads. If another
      // user is still persisted (session ended without logout), reset first so identify()
      // does not link the two identities through $anon_distinct_id / $device_id.
      const current = this.sdk.get_distinct_id();
      if (distinctId && current && current !== distinctId && !String(current).startsWith('$device:')) {
        this.sdk.reset();
      }
      if (distinctId) this.sdk.identify(distinctId);
      this.active = true;
      this.flushPending();
      if (this.replayRequested) this.startSessionRecording();
    } catch (error) {
      console.error('mixpanel init error', error);
      if (generation === this.generation && !this.active) this.closeBuffer();
    }
  }

  private send(event: string, props: Record<string, unknown>, options: TrackOptions = {}): void {
    try {
      const properties = { ...this.baseProps(), subProperties: props };
      if (options.beacon) this.sdk.track(event, properties, BEACON_REQUEST);
      else this.sdk.track(event, properties);
    } catch (error) {
      console.error('mixpanel track error', error);
    }
  }

  /**
   * Keeps an event while the init is in flight (or about to start, see MIXPANEL_PENDING_GRACE_MS).
   * Nothing is kept when Mixpanel is disabled or the last init ended without activation.
   */
  private buffer(event: string, props: Record<string, unknown>, options: TrackOptions): void {
    if (!this.enabled || this.bufferClosed || this.pending.length >= MIXPANEL_PENDING_LIMIT) return;
    this.pending.push({ event, props, options });
    if (!this.initRun && !this.graceTimer) {
      this.graceTimer = setTimeout(() => {
        this.graceTimer = undefined;
        if (!this.initRun && !this.active) this.pending = [];
      }, MIXPANEL_PENDING_GRACE_MS);
    }
  }

  private flushPending(): void {
    const events = this.pending;
    this.dropPending();
    events.forEach(({ event, props, options }) => this.send(event, props, options));
  }

  private dropPending(): void {
    this.pending = [];
    if (this.graceTimer) {
      clearTimeout(this.graceTimer);
      this.graceTimer = undefined;
    }
  }

  /** The init ended without activation: drop what was kept and stop buffering until the next init. */
  private closeBuffer(): void {
    this.dropPending();
    this.bufferClosed = true;
  }

  private sdkConfig(): Partial<Config> {
    return {
      persistence: 'localStorage',
      // Only catalog events are sent (added in later phases).
      autocapture: false,
      track_pageview: false,
      // Replay is started manually on specific screens after our own sampling.
      // Default text masking is kept (no record_mask_text_selector override).
      record_sessions_percent: 0,
      debug: false,
    };
  }

  private baseProps(): MixpanelBaseProps {
    return {
      userRole: this.userRole,
      dataMode: this.mockToggle.isMockEnabled() ? 'mock' : 'api',
      appEnvironment: this.config.appEnvironment,
    };
  }

  private isReplaySampled(): boolean {
    this.replaySampled ??= Math.random() * 100 < this.config.replayPercent;
    return this.replaySampled;
  }
}
